import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  ReactNode,
} from "react";

interface ConfirmDialogProps {
  title?: string;
  description?: string;
  content?: ReactNode;
  setResult?: (value: boolean) => void;
  destructive?: boolean;
  shouldResetOnCancel?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  centerButtons?: boolean;
  modalSize?: "sm" | "md" | "lg" | "xl";
  hideCancelButton?: boolean;
}

export interface ConfirmDialogRef {
  openDialog: (options?: {
    title?: string;
    description?: string;
    content?: ReactNode;
    shouldResetOnCancel?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;
    centerButtons?: boolean;
    modalSize?: "sm" | "md" | "lg" | "xl";
    hideCancelButton?: boolean;
  }) => void;
}

const ConfirmDialog = memo(
  forwardRef<ConfirmDialogRef, ConfirmDialogProps>(
    (
      {
        title: defaultTitle,
        description: defaultDescription,
        content: defaultContent,
        setResult,
        destructive,
        shouldResetOnCancel: defaultShouldResetOnCancel = false,
        confirmButtonText: defaultConfirmButtonText = "Confirm",
        cancelButtonText: defaultCancelButtonText = "Cancel",
        centerButtons: defaultCenterButtons = false,
        modalSize: defaultModalSize = "lg",
        hideCancelButton: defaultHideCancelButton = false,
      },
      ref
    ) => {
      const { isOpen, onOpen, onClose } = useDisclosure();
      const [dialogTitle, setDialogTitle] = useState(defaultTitle);
      const [dialogDescription, setDialogDescription] =
        useState(defaultDescription);
      const [dialogContent, setDialogContent] =
        useState<ReactNode>(defaultContent);
      const [currentShouldResetOnCancel, setCurrentShouldResetOnCancel] =
        useState(defaultShouldResetOnCancel);
      const [confirmText, setConfirmText] = useState(defaultConfirmButtonText);
      const [cancelText, setCancelText] = useState(defaultCancelButtonText);
      const [centerButtons, setCenterButtons] = useState(defaultCenterButtons);
      const [modalSize, setModalSize] = useState(defaultModalSize);
      const [hideCancelButton, setHideCancelButton] = useState(defaultHideCancelButton);

      const resetToDefaults = useCallback(() => {
        setDialogTitle(defaultTitle);
        setDialogDescription(defaultDescription);
        setDialogContent(defaultContent);
        setConfirmText(defaultConfirmButtonText);
        setCancelText(defaultCancelButtonText);
        setCenterButtons(defaultCenterButtons);
        setModalSize(defaultModalSize);
        setHideCancelButton(defaultHideCancelButton);
      }, [
        defaultTitle,
        defaultDescription,
        defaultContent,
        defaultConfirmButtonText,
        defaultCancelButtonText,
        defaultCenterButtons,
        defaultModalSize,
        defaultHideCancelButton,
      ]);

      const handleCancel = useCallback(() => {
        onClose();
        setResult?.(false);
        if (currentShouldResetOnCancel) {
          resetToDefaults();
        }
      }, [onClose, setResult, currentShouldResetOnCancel, resetToDefaults]);

      const handleAccept = useCallback(() => {
        onClose();
        setResult?.(true);
      }, [onClose, setResult]);

      const openDialog = useCallback(
        (options?: {
          title?: string;
          description?: string;
          content?: ReactNode;
          shouldResetOnCancel?: boolean;
          confirmButtonText?: string;
          cancelButtonText?: string;
          centerButtons?: boolean;
          modalSize?: "sm" | "md" | "lg" | "xl";
          hideCancelButton?: boolean;
        }) => {
          setDialogTitle(options?.title || defaultTitle);
          setDialogDescription(options?.description || defaultDescription);
          setDialogContent(
            options?.content !== undefined ? options.content : defaultContent
          );
          setCurrentShouldResetOnCancel(
            options?.shouldResetOnCancel !== undefined
              ? options.shouldResetOnCancel
              : defaultShouldResetOnCancel
          );
          setConfirmText(
            options?.confirmButtonText || defaultConfirmButtonText
          );
          setCancelText(options?.cancelButtonText || defaultCancelButtonText);
          setCenterButtons(
            options?.centerButtons !== undefined
              ? options.centerButtons
              : defaultCenterButtons
          );
          setModalSize(options?.modalSize || defaultModalSize);
          setHideCancelButton(options?.hideCancelButton || defaultHideCancelButton);
          onOpen();
        },
        [
          onOpen,
          defaultTitle,
          defaultDescription,
          defaultContent,
          defaultShouldResetOnCancel,
          defaultConfirmButtonText,
          defaultCancelButtonText,
          defaultCenterButtons,
          defaultModalSize,
          defaultHideCancelButton,
        ]
      );

      useImperativeHandle(ref, () => ({
        openDialog,
      }));

      return (
        <Modal
          isOpen={isOpen}
          onClose={handleCancel}
          isCentered
          size={modalSize}
        >
          <ModalOverlay />
          <ModalContent>
            {dialogTitle && <ModalHeader>{dialogTitle}</ModalHeader>}
            <ModalCloseButton />
            <ModalBody gap={4}>
              <Flex gap={2} direction={"column"}>
                {dialogDescription && <Text>{dialogDescription}</Text>}
                {dialogContent && <>{dialogContent}</>}
              </Flex>
            </ModalBody>
            <ModalFooter
              gap={2}
              justifyContent={centerButtons ? "center" : "flex-end"}
            >
              {hideCancelButton ? (
                <Button
                  bgColor={destructive ? "red.500" : "brand.500"}
                  color={"white"}
                  borderRadius={8}
                  _hover={{
                    backgroundColor: destructive ? "red.600" : "brand.600",
                  }}
                  onClick={handleAccept}
                  autoFocus
                >
                  {confirmText}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleCancel}
                    borderColor={destructive ? "red.500" : "brand.500"}
                    variant="outline"
                    borderRadius={8}
                    color={destructive ? "red.500" : "brand.500"}
                  >
                    {cancelText}
                  </Button>
                  <Button
                    bgColor={destructive ? "red.500" : "brand.500"}
                    color={"white"}
                    borderRadius={8}
                    _hover={{
                      backgroundColor: destructive ? "red.600" : "brand.600",
                    }}
                    onClick={handleAccept}
                    autoFocus
                  >
                    {confirmText}
                  </Button>
                </>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      );
    }
  )
);

export default ConfirmDialog;

type UseConfirmDialogProps = Omit<ConfirmDialogProps, "setResult">;

export const useConfirmDialog = (props: UseConfirmDialogProps = {}) => {
  const ref = React.useRef<ConfirmDialogRef>(null);
  const [resolveResult, setResolveResult] =
    useState<(value: boolean) => void>();

  const openDialog = useCallback(
    (options?: {
      title?: string;
      description?: string;
      content?: ReactNode;
      shouldResetOnCancel?: boolean;
      confirmButtonText?: string;
      cancelButtonText?: string;
      centerButtons?: boolean;
      modalSize?: "sm" | "md" | "lg" | "xl";
    }) => {
      return new Promise<boolean>((resolve) => {
        setResolveResult(() => resolve);
        ref.current?.openDialog(options);
      });
    },
    []
  );

  const ConfirmDialogComponent = useMemo(() => {
    return (
      <ConfirmDialog
        {...props}
        ref={ref}
        setResult={(value) => {
          resolveResult?.(value);
        }}
      />
    );
  }, [props, resolveResult]);

  return {
    ConfirmDialog: ConfirmDialogComponent,
    openDialog,
  };
};
